import type { RatingWithColor } from '@risksmart-app/components/src/hooks/types';
import type { RatingContext } from '@risksmart-app/components/src/hooks/useRating';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { useCallback, useMemo } from 'react';

import type { ConfigType } from '@/hooks/queries/risk-assessment-result-config/types';

import {
  bandDimensionValue,
  numericRatingOptions,
} from './bandedRatingLookups';
import { useScoringSettings } from './useScoringSettings';

export interface ResolvedRating {
  label: string;
  value: number;
  color?: string;
}

export interface RiskRatingResolver {
  resolveRiskRating: (params: {
    likelihood: number | null | undefined;
    impact: number | null | undefined;
    controlType: Risk_Assessment_Result_Control_Type_Enum;
    rating: number | null | undefined;
    configId?: string | null;
  }) => ResolvedRating | undefined;
  resolveImpact: (
    value: number | null | undefined,
    configId?: string | null
  ) => ResolvedRating | undefined;
  resolveLikelihood: (
    value: number | null | undefined,
    configId?: string | null
  ) => ResolvedRating | undefined;
  hasScoringSettings: boolean;
  maxInherentRating: number;
  maxResidualRating: number;
  options: {
    likelihood: RatingWithColor<number>[];
    impact: RatingWithColor<number>[];
    riskRating: RatingWithColor<number>[];
  };
}

export const useRiskRatingResolver = (
  context: RatingContext = 'standard',
  options: { configType: ConfigType; configIds?: string[]; skip?: boolean }
): RiskRatingResolver => {
  const {
    hasScoringSettings,
    getImpactCategories,
    getRatingByLikelihoodAndImpact,
    getLikelihoodByValue,
    getImpactByValue,
    getRiskRatingByValue,
    likelihoodOptions,
    impactOptions,
    riskRatingOptions,
  } = useScoringSettings({
    configType: options.configType,
    configIds: options.configIds,
    skip: options.skip,
  });

  const {
    getByValue: getTaxonomyControlledRatingByValue,
    options: taxonomyControlledOptions,
  } = useRating('risk_controlled', context);

  const {
    getByValue: getTaxonomyUncontrolledRatingByValue,
    options: taxonomyUncontrolledOptions,
  } = useRating('risk_uncontrolled', context);

  const { options: taxonomyLikelihoodOptions } = useRating(
    'likelihood',
    context
  );

  const { options: taxonomyImpactOptions } = useRating('impact', context);

  // The taxonomy offers only exact value lookups, so its dimension scales are
  // banded through the same rule as the matrix config (RSP-7284).
  const taxonomyLikelihoodScale = useMemo(
    () => numericRatingOptions(taxonomyLikelihoodOptions),
    [taxonomyLikelihoodOptions]
  );
  const taxonomyImpactScale = useMemo(
    () => numericRatingOptions(taxonomyImpactOptions),
    [taxonomyImpactOptions]
  );

  const resolveRiskRating = useCallback(
    ({
      likelihood,
      impact,
      controlType,
      rating,
      configId,
    }: {
      likelihood: number | null | undefined;
      impact: number | null | undefined;
      controlType: Risk_Assessment_Result_Control_Type_Enum;
      rating: number | null | undefined;
      configId?: string | null;
    }): ResolvedRating | undefined => {
      if (hasScoringSettings) {
        const cell =
          likelihood != null && impact != null
            ? getRatingByLikelihoodAndImpact(likelihood, impact, configId)
            : undefined;

        if (context === 'standard') {
          if (!cell) {
            return undefined;
          }

          // Single-impact scoring honours a manual override: when the stored
          // rating diverges from the computed cell, show it, resolved against
          // the version it was assessed under. Multi-impact never overrides.
          // Cardinality is read from the rating's own version (an org can switch
          // single ↔ multi between versions), not the latest config.
          const isMultiImpact = getImpactCategories(configId).length >= 2;
          if (!isMultiImpact && rating != null && rating !== cell.value) {
            const band = getRiskRatingByValue(rating, configId);
            if (band) {
              return {
                label: band.label,
                value: band.value,
                color: band.color,
              };
            }
          }

          return { label: cell.label, value: cell.value, color: cell.color };
        }

        // Oversight / internal audit: prefer the cell when likelihood/impact
        // resolve, otherwise the discrete rating is the source of truth and may
        // be set directly with no likelihood/impact.
        if (cell) {
          return { label: cell.label, value: cell.value, color: cell.color };
        }

        if (rating != null) {
          const ratingOption = riskRatingOptions.find(
            (o) => o.value === rating
          );
          if (ratingOption) {
            return {
              label: ratingOption.label,
              value: ratingOption.value,
              color: ratingOption.color,
            };
          }
        }

        return undefined;
      }

      const option =
        controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled
          ? getTaxonomyControlledRatingByValue(rating)
          : getTaxonomyUncontrolledRatingByValue(rating);

      if (!option || option.value == null) {
        return undefined;
      }

      return {
        label: option.label,
        value: Number(option.value),
        color: 'color' in option ? (option.color as string) : undefined,
      };
    },
    [
      context,
      hasScoringSettings,
      getImpactCategories,
      getRatingByLikelihoodAndImpact,
      getRiskRatingByValue,
      riskRatingOptions,
      getTaxonomyControlledRatingByValue,
      getTaxonomyUncontrolledRatingByValue,
    ]
  );

  const resolveImpact = useCallback(
    (
      value: number | null | undefined,
      configId?: string | null
    ): ResolvedRating | undefined => {
      if (value == null) {
        return undefined;
      }

      if (hasScoringSettings) {
        const r = getImpactByValue(value, configId);

        return r
          ? { label: r.label, value: r.value, color: r.color }
          : undefined;
      }

      const option = bandDimensionValue(taxonomyImpactScale, value);

      return option
        ? { label: option.label, value: option.value, color: option.color }
        : undefined;
    },
    [hasScoringSettings, getImpactByValue, taxonomyImpactScale]
  );

  const resolveLikelihood = useCallback(
    (
      value: number | null | undefined,
      configId?: string | null
    ): ResolvedRating | undefined => {
      if (value == null) {
        return undefined;
      }

      if (hasScoringSettings) {
        const r = getLikelihoodByValue(value, configId);

        return r
          ? { label: r.label, value: r.value, color: r.color }
          : undefined;
      }

      const option = bandDimensionValue(taxonomyLikelihoodScale, value);

      return option
        ? { label: option.label, value: option.value, color: option.color }
        : undefined;
    },
    [hasScoringSettings, getLikelihoodByValue, taxonomyLikelihoodScale]
  );

  const resolverOptions = useMemo(
    () => ({
      likelihood: hasScoringSettings
        ? likelihoodOptions
        : taxonomyLikelihoodOptions.map((option) => ({
            label: option.label,
            value: Number(option.value),
            color: option.color ?? '',
          })),
      impact: hasScoringSettings
        ? impactOptions
        : taxonomyImpactOptions.map((option) => ({
            label: option.label,
            value: Number(option.value),
            color: option.color ?? '',
          })),
      riskRating: riskRatingOptions,
    }),
    [
      hasScoringSettings,
      likelihoodOptions,
      impactOptions,
      taxonomyLikelihoodOptions,
      taxonomyImpactOptions,
      riskRatingOptions,
    ]
  );

  const maxInherentRating = useMemo(() => {
    if (hasScoringSettings) {
      return Math.max(...riskRatingOptions.map((o) => o.value), 0);
    }

    return Math.max(
      ...taxonomyUncontrolledOptions.map((o) => Number(o.value) || 0)
    );
  }, [hasScoringSettings, riskRatingOptions, taxonomyUncontrolledOptions]);

  const maxResidualRating = useMemo(() => {
    if (hasScoringSettings) {
      return Math.max(...riskRatingOptions.map((o) => o.value), 0);
    }

    return Math.max(
      ...taxonomyControlledOptions.map((o) => Number(o.value) || 0)
    );
  }, [hasScoringSettings, riskRatingOptions, taxonomyControlledOptions]);

  return useMemo(
    () => ({
      resolveRiskRating,
      resolveImpact,
      resolveLikelihood,
      hasScoringSettings,
      maxInherentRating,
      maxResidualRating,
      options: resolverOptions,
    }),
    [
      resolveRiskRating,
      resolveImpact,
      resolveLikelihood,
      hasScoringSettings,
      maxInherentRating,
      maxResidualRating,
      resolverOptions,
    ]
  );
};
